import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet, View, Platform, Animated } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { useUserBanners } from '@/hooks/useUserBanners';
import { trackEvent } from '@/constants/utils/analytics';
import { useRouter } from 'expo-router';
import { useModalStore } from '@/store';
import { devLog } from '@/lib/devLog';
import TooltipCardBody, { ARROW_SIZE, getTooltipAccent, getTooltipCardBg } from './TooltipCardBody';

const createStyles = (colors, isDark, position) => {
  const cardBg = getTooltipCardBg(colors);
  const isAbove = position === 'above';

  return StyleSheet.create({
    container: {
      position: 'absolute',
      ...(isAbove
        ? { bottom: '100%', marginBottom: ARROW_SIZE + 5 }
        : { top: '100%', marginTop: ARROW_SIZE + 5 }),
      right: -20,
      zIndex: 1000,
      width: 280,
    },
    arrow: {
      position: 'absolute',
      right: 36,
      width: 0,
      height: 0,
      borderLeftWidth: ARROW_SIZE,
      borderLeftColor: 'transparent',
      borderRightWidth: ARROW_SIZE,
      borderRightColor: 'transparent',
      zIndex: 1,
      ...(isAbove
        ? { bottom: -(ARROW_SIZE - 1), borderTopWidth: ARROW_SIZE, borderTopColor: cardBg }
        : { top: -(ARROW_SIZE - 1), borderBottomWidth: ARROW_SIZE, borderBottomColor: cardBg }),
    },
  });
};

function TooltipBanner({ anchorId, position = 'below', style, banner: bannerProp, onDismiss: onDismissProp }) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const { banners, getTooltipBanner, markSeen, markClicked, dismiss } = useUserBanners();
  const router = useRouter();
  const { openModal } = useModalStore(state => state.actions);

  const isDirectMode = !!bannerProp;
  const banner = isDirectMode ? bannerProp : getTooltipBanner(anchorId);
  const accentColor = getTooltipAccent(banner?.contentType, colors);
  const styles = useMemo(() => createStyles(colors, isDark, position), [colors, isDark, position]);

  if (!isDirectMode) {
    devLog('[TooltipBanner]', anchorId, '→ found:', banner ? banner.content?.headline : 'none', '| total banners:', banners.length);
  }

  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(position === 'above' ? 10 : -10)).current;
  const hasFiredSeen = useRef(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (banner && !hasFiredSeen.current) {
      hasFiredSeen.current = true;
      if (!isDirectMode) markSeen(banner.id);

      Animated.parallel([
        Animated.timing(fadeAnim, { toValue: 1, duration: 350, useNativeDriver: true }),
        Animated.spring(slideAnim, { toValue: 0, tension: 50, friction: 9, useNativeDriver: true }),
      ]).start();
    }
  }, [banner]);

  const handleDismiss = useCallback(() => {
    if (!banner) return;
    trackEvent('engagement', 'banner_dismissed', 'tooltip', banner.id);
    Animated.parallel([
      Animated.timing(fadeAnim, { toValue: 0, duration: 200, useNativeDriver: true }),
      Animated.timing(slideAnim, { toValue: position === 'above' ? 10 : -10, duration: 200, useNativeDriver: true }),
    ]).start(() => {
      setDismissed(true);
      if (isDirectMode) {
        onDismissProp?.(banner.id);
      } else {
        dismiss(banner.id);
      }
    });
  }, [banner, dismiss, onDismissProp, isDirectMode, fadeAnim, slideAnim, position]);

  const handleCTA = useCallback(() => {
    if (!banner) return;
    trackEvent('conversion', 'banner_cta_clicked', 'tooltip', banner.id);

    if (!isDirectMode) {
      markClicked(banner.id);
      if (banner.content.ctaUrl) {
        if (banner.content.ctaUrl.startsWith('/')) {
          router.push(banner.content.ctaUrl);
        } else if (banner.content.ctaUrl.startsWith('modal:')) {
          openModal(banner.content.ctaUrl.replace('modal:', ''));
        }
      }
      dismiss(banner.id);
    } else {
      onDismissProp?.(banner.id);
    }
  }, [banner, router, openModal, dismiss, markClicked, onDismissProp, isDirectMode]);

  if (!banner || dismissed) return null;

  const { content, isDismissible } = banner;

  return (
    <Animated.View
      style={[
        styles.container,
        style,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <TooltipCardBody
        content={content}
        contentType={banner.contentType}
        isDismissible={isDismissible}
        accentColor={accentColor}
        colors={colors}
        isDark={isDark}
        theme={theme}
        onDismiss={handleDismiss}
        onCTA={handleCTA}
      />
      <View style={styles.arrow} />
    </Animated.View>
  );
}

export default React.memo(TooltipBanner);
